/**
 * Post Editor — shared constants, palettes, fonts and layout helpers.
 * Extracted from the original PostEditor.jsx so Canvas, Toolbar and Layer
 * modules can import them without pulling in the full page component.
 */

export const LOGO_POSITIONS = [
  ["bottom-right", "Sağ alt"],
  ["bottom-left",  "Sol alt"],
  ["top-right",    "Sağ üst"],
  ["top-left",     "Sol üst"],
  ["center",       "Orta"],
];

export const FONT_FAMILIES = [
  ["inter",         "Inter",          "'Inter', ui-sans-serif, system-ui, sans-serif"],
  ["playfair",      "Playfair",       "'Playfair Display', ui-serif, Georgia, serif"],
  ["montserrat",    "Montserrat",     "'Montserrat', ui-sans-serif, system-ui, sans-serif"],
  ["oswald",        "Oswald",         "'Oswald', 'Impact', ui-sans-serif, sans-serif"],
  ["bebas",         "Bebas Neue",     "'Bebas Neue', 'Oswald', ui-sans-serif, sans-serif"],
  ["poppins",       "Poppins",        "'Poppins', ui-sans-serif, system-ui, sans-serif"],
  ["roboto-slab",   "Roboto Slab",    "'Roboto Slab', ui-serif, Georgia, serif"],
  ["lora",          "Lora",           "'Lora', ui-serif, Georgia, serif"],
  ["space-grotesk", "Space Grotesk",  "'Space Grotesk', ui-sans-serif, system-ui, sans-serif"],
  ["archivo-black", "Archivo Black",  "'Archivo Black', 'Impact', ui-sans-serif, sans-serif"],
];
export const FONT_CSS_MAP = Object.fromEntries(FONT_FAMILIES.map(([k, , css]) => [k, css]));
export const FONT_LABEL_MAP = Object.fromEntries(FONT_FAMILIES.map(([k, lb]) => [k, lb]));

// Zengin palet · her satır bir kategori. Son kullanılanlar localStorage'da tutulur.
export const PALETTE_GROUPS = [
  ["Beyaz & Siyah", ["#ffffff", "#f1f5f9", "#cbd5e1", "#64748b", "#334155", "#111827", "#000000"]],
  ["Kırmızı · Pembe", ["#fee2e2", "#fecaca", "#f87171", "#ef4444", "#dc2626", "#b91c1c", "#7f1d1d", "#f472b6", "#ec4899", "#db2777", "#be185d"]],
  ["Turuncu · Sarı",  ["#fed7aa", "#fdba74", "#fb923c", "#f97316", "#ea580c", "#9a3412", "#fef08a", "#fde047", "#facc15", "#eab308", "#ca8a04"]],
  ["Yeşil · Teal",    ["#bbf7d0", "#86efac", "#4ade80", "#22c55e", "#16a34a", "#15803d", "#14532d", "#99f6e4", "#5eead4", "#2dd4bf", "#0d9488"]],
  ["Mavi · İndigo",   ["#bfdbfe", "#93c5fd", "#60a5fa", "#3b82f6", "#2563eb", "#1d4ed8", "#1e3a8a", "#c7d2fe", "#a5b4fc", "#818cf8", "#6366f1", "#4338ca"]],
  ["Mor · Fuşya",     ["#e9d5ff", "#d8b4fe", "#c084fc", "#a855f7", "#9333ea", "#7e22ce", "#581c87", "#f5d0fe", "#f0abfc", "#e879f9", "#d946ef"]],
];
export const PALETTE_FLAT = PALETTE_GROUPS.flatMap(([, cs]) => cs);
export const RECENT_COLORS_KEY = "pe:recentColors";

export function readRecentColors() {
  try { return JSON.parse(localStorage.getItem(RECENT_COLORS_KEY) || "[]").slice(0, 12); }
  catch { return []; }
}
export function pushRecentColor(c) {
  try {
    const list = readRecentColors().filter((x) => x.toLowerCase() !== c.toLowerCase());
    list.unshift(c);
    localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(list.slice(0, 12)));
  } catch { /* ignore */ }
}

export const DEFAULT_PHOTO  = { x: 0.78, y: 0.5,  w: 0.4, h: 0.75, shape: "rect" };

export const makeLayerId = () => "ly_" + Math.random().toString(36).slice(2, 9);

// Standart yerleşim: sol-üst alt alta başlık (soldaki ~55/85% alan).
// idx 0 → en büyük başlık; sonraki satırlar sırayla altına, ritmik hiyerarşiyle.
// Renk ritmi: tek-çift satırlar beyaz/kırmızı vurgu (örnek görseldeki akış).
export const AUTO_SIZE_LADDER = [0.11, 0.085, 0.085, 0.085, 0.065, 0.06, 0.055, 0.05, 0.045, 0.04];
export const AUTO_COLOR_LADDER = ["#ffffff", "#DC2626", "#ffffff", "#DC2626", "#ffffff", "#DC2626"];
export const AUTO_START_Y = 0.06;
// Y boşluğu: önceki satırın size'ı × bu çarpan (iki satıra bölünen metinler için güvenli).
export const AUTO_LINE_FACTOR = 1.55;
export const MW_WITH_PHOTO = 0.6;
export const MW_NO_PHOTO   = 0.85;

export function autoSize(i) {
  return AUTO_SIZE_LADDER[Math.min(i, AUTO_SIZE_LADDER.length - 1)];
}

export function autoY(idx) {
  let y = AUTO_START_Y;
  for (let i = 0; i < idx; i++) y += autoSize(i) * AUTO_LINE_FACTOR;
  return Math.min(0.92, y);
}

export function autoLayout(idx, hasPhoto = false) {
  return {
    font_family: "inter",
    bold: true,
    italic: false,
    underline: false,
    size: autoSize(idx),
    color: AUTO_COLOR_LADDER[idx % AUTO_COLOR_LADDER.length],
    x: 0.05,
    y: autoY(idx),
    align: "left",
    max_width: hasPhoto ? MW_WITH_PHOTO : MW_NO_PHOTO,
    shadow: false,
  };
}

export function freshLayer(partial = {}, idx = 0, hasPhoto = false) {
  return {
    id: makeLayerId(),
    text: "",
    _mwAuto: true,
    hidden: false,
    locked: false,
    ...autoLayout(idx, hasPhoto),
    ...partial,
  };
}

export const INITIAL_LAYERS = () => [
  freshLayer({ text: "" }, 0),
  freshLayer({ text: "" }, 1),
];

export function absUrl(url) {
  if (!url) return url;
  if (/^https?:/.test(url)) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  return `${base}${url}`;
}

// Snap tolerance used by the canvas drag logic.
export const SNAP_T = 0.012;
export const CANVAS_SNAP = [0, 0.5, 1];
